import { useState, useEffect, useCallback, useRef } from 'react';
import { accountService } from '@/features/card/services/accountService/accountService';
import type { Card, UseCardsReturn } from '@/features/card/types/card.types';
import { formatDateTimeExact } from '@chikara/utils';
import { isBasic } from '@/shared/config/permissions';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useAsyncGuard } from '@chikara/hooks';
import { useSpinner } from '@chikara/ui';
import { t } from '@/i18n';


export const useCards = (): UseCardsReturn => {
    const { userData, payload: authPayload } = useAuth();
    const { show, hide } = useSpinner();

    // We use refs for auth data to keep fetchCards stable and avoid identity-based loops
    const authRef = useRef({
        userUuid: authPayload?.user_uuid,
        role: userData?.role,
        isBasic: isBasic(userData?.role)
    });

    // Update refs whenever auth data changes, but don't re-create functions
    useEffect(() => {
        authRef.current = {
            userUuid: authPayload?.user_uuid,
            role: userData?.role,
            isBasic: isBasic(userData?.role)
        };
    }, [authPayload?.user_uuid, userData?.role]);

    const [cards, setCards] = useState<Card[]>([]);
    const [currentStatus, setCurrentStatus] = useState<string | undefined>('enabled');
    const [error, setError] = useState<string | null>(null);
    const [paginationData, setPaginationData] = useState({
        currentPage: 1,
        totalPages: 1,
        itemsPerPage: 10,
        totalItems: 0,
    });

    const { checkGuard } = useAsyncGuard();

    const fetchCards = useCallback(async (page: number = 1, limit: number = 10, status?: string) => {
        if (!checkGuard()) return;

        show();
        setError(null);
        try {
            const { userUuid, isBasic: basicUser } = authRef.current;
            const effectiveStatus = status !== undefined ? status : (basicUser ? undefined : currentStatus);

            if (status !== undefined && !basicUser) {
                setCurrentStatus(status);
            }

            const params = {
                page,
                limit,
                ...(effectiveStatus && { status: effectiveStatus }),
                ...(basicUser && { user_uuid: userUuid })
            }

            const response = await accountService.listAccounts(params);
            const cardList = response.data.accounts.map((item: any) => {
                return {
                    ...item,
                    email: item.user?.email,
                    cardAlias: item.card?.metadata?.alias || 'N/A',
                    maskedPan: item.card?.pan_masked ? `•••• ${String(item.card?.pan_masked).slice(-4)}` : '•••• 0000',
                    cardholderName: item.user?.fullname,
                    disposable: item.balance?.disposable || 0,
                    creditLimit: item.balance?.limit_assigned || 0,
                    type: item.card?.type || 'N/A',
                    tag: item.card?.metadata?.tag || 'N/A',
                    status: item.card?.status || (item.status !== 'enabled' ? item.status : 'no_card'),
                    cdate: formatDateTimeExact(item.cdate),
                    card_uuid: item.card?.uuid,
                    card_status: item.card?.status,
                    last_activity: item.card?.last_activity || 'N/A',
                }
            })

            setCards(cardList || []);
            setPaginationData(prev => ({
                ...prev,
                currentPage: response.data.pagination.current_page,
                totalPages: response.data.pagination.total_pages,
                itemsPerPage: response.data.pagination.limit,
                totalItems: response.data.pagination.total_records,
            }));
        } catch (err) {
            setError(t('card.error.getCards'));
            console.error(err);
        } finally {
            hide();
        }
    }, [show, hide, currentStatus,
    ]);

    const handlePageChange = useCallback((page: number, limit: number) => {
        fetchCards(page, limit);
    }, [fetchCards]);

    const pagination = {
        ...paginationData,
        onPageChange: handlePageChange
    };

    useEffect(() => {
        fetchCards();
    }, [fetchCards]);

    return {
        cards,
        error,
        pagination,
        refetch: fetchCards,
    };
};
